import { useState, useCallback } from 'react';
import { usePaywallGate } from '../../../utils/paywall';

interface NettingResult {
  batchNo: string;
  nettingAmount: number;
  mode: 'order' | 'amount';
  customerName: string;
  needsApproval: boolean;
  nettingLogId: string;
  orderCount: number;
}

interface Props {
  result: NettingResult;
  onClose: () => void;
}

function fmt(n: number) {
  return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function generateNettingText(result: NettingResult): string {
  const lines: string[] = [];
  lines.push('📋 订单抵消确认单');
  lines.push('──────────────────');
  lines.push(`批次号：${result.batchNo}`);
  lines.push(`抵消模式：${result.mode === 'order' ? '订单抵消' : '金额抵消'}`);
  lines.push(`客户：${result.customerName}`);
  lines.push(`抵消金额：¥${fmt(result.nettingAmount)}`);
  lines.push(`涉及订单：${result.orderCount} 笔`);
  lines.push('');
  if (result.needsApproval) {
    lines.push('⏳ 状态：待对方审核确认');
    lines.push('');
    lines.push('请将此信息转发给对方，对方可通过系统通知');
    lines.push('进入审核页面确认是否同意本次抵消。');
  } else {
    lines.push('✅ 状态：已生效');
  }
  lines.push('');
  lines.push('──────────────────');
  lines.push(`生成时间：${new Date().toLocaleString('zh-CN')}`);
  return lines.join('\n');
}

export default function NettingSuccessModal({ result, onClose }: Props) {
  const [copied, setCopied] = useState(false);
  const [exportError, setExportError] = useState('');
  const gate = usePaywallGate();

  const summaryText = generateNettingText(result);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(summaryText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      const el = document.createElement('textarea');
      el.value = summaryText;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  }, [summaryText]);

  const handleExportImage = useCallback(async () => {
    setExportError('');
    if (!(await gate('exportPrint', '导出图片'))) return;
    try {
      const html2canvas = (await import('html2canvas')).default;
      const target = document.getElementById('netting-summary-card');
      if (!target) {
        setExportError('摘要卡片尚未渲染完成，请稍后重试');
        return;
      }
      const canvas = await html2canvas(target, { scale: 2, backgroundColor: '#ffffff', logging: false });
      const link = document.createElement('a');
      link.download = `netting-${result.batchNo}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {
      // 拍图失败不能再什么都不说：这个弹窗是抵消提交后的唯一出口，静默失败等于按钮坏了
      setExportError(e instanceof Error && e.message ? `导出图片失败：${e.message}` : '导出图片失败，请稍后重试');
    }
  }, [gate, result.batchNo]);

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50">
      <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${result.needsApproval ? 'bg-amber-50' : 'bg-teal-50'}`}>
              <i className={`${result.needsApproval ? 'ri-time-line text-amber-600' : 'ri-check-double-line text-teal-600'} text-base`} />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {result.needsApproval ? '抵消申请已提交' : '抵消已完成'}
              </p>
              <p className="text-xs text-gray-400 font-mono">{result.batchNo}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer transition-colors rounded-lg hover:bg-gray-50">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 flex-1 overflow-y-auto space-y-4">
          {/* Approval notice */}
          {result.needsApproval && (
            <div className="flex items-start gap-2.5 px-3.5 py-3 bg-amber-50 rounded-xl border border-amber-100">
              <i className="ri-error-warning-line text-amber-500 text-base mt-0.5 shrink-0" />
              <div className="text-xs text-amber-700 leading-relaxed">
                <p className="font-semibold mb-1">请及时告知对方进行审核</p>
                <p>客户方需要对本次抵消进行复核确认，请将以下信息发送给对方，确认是否存在抵消问题、是否同意相互之间的订单抵消。</p>
              </div>
            </div>
          )}

          {!result.needsApproval && (
            <div className="flex items-start gap-2.5 px-3.5 py-3 bg-teal-50 rounded-xl border border-teal-100">
              <i className="ri-check-line text-teal-500 text-base mt-0.5 shrink-0" />
              <div className="text-xs text-teal-700 leading-relaxed">
                <p>抵消已直接生效，相关订单的付款金额和状态已更新。</p>
              </div>
            </div>
          )}

          {/* Summary card */}
          <div id="netting-summary-card" className="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">抵消模式</span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                result.mode === 'order'
                  ? 'bg-teal-50 text-teal-700 border border-teal-200'
                  : 'bg-amber-50 text-amber-700 border border-amber-200'
              }`}>
                {result.mode === 'order' ? '订单抵消' : '金额抵消'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">客户</span>
              <span className="text-xs font-medium text-gray-800">{result.customerName}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">抵消金额</span>
              <span className="text-sm font-bold text-teal-600">¥{fmt(result.nettingAmount)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">涉及订单</span>
              <span className="text-xs font-medium text-gray-800">{result.orderCount} 笔</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">状态</span>
              <span className={`text-xs font-medium ${result.needsApproval ? 'text-amber-600' : 'text-teal-600'}`}>
                {result.needsApproval ? '待审核' : '已生效'}
              </span>
            </div>
          </div>

          {/* Text preview (for sharing) */}
          {result.needsApproval && (
            <div>
              <p className="text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                <i className="ri-file-text-line text-xs" />
                可分享的文字摘要
              </p>
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-3 font-mono text-xs text-gray-700 leading-relaxed whitespace-pre-wrap max-h-36 overflow-y-auto">
                {summaryText}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-5 py-4 border-t border-gray-100 shrink-0 space-y-2.5">
          {/* Share & Export (premium only) */}
          {result.needsApproval && (
            <div className="flex gap-2">
              {/* Copy text button - always available */}
              <button
                onClick={handleCopy}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-all whitespace-nowrap ${
                  copied
                    ? 'bg-teal-500 text-white'
                    : 'bg-teal-500 hover:bg-teal-600 text-white'
                }`}
              >
                <i className={`${copied ? 'ri-check-line' : 'ri-clipboard-line'} text-sm`} />
                {copied ? '已复制' : '复制文字摘要'}
              </button>

              {/* 导出图片：会员墙判在点击那一刻，按钮恒为正常态（会员状态是异步拉的，
                  拿它禁用按钮会让尊享用户在状态落地前也看到灰态，还会闪一下） */}
              <button
                onClick={() => { void handleExportImage(); }}
                className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-all bg-slate-700 hover:bg-slate-800 text-white whitespace-nowrap"
              >
                <i className="ri-image-line text-sm" />
                导出图片
              </button>
            </div>
          )}

          {exportError && (
            <p className="text-xs text-center text-rose-600 leading-relaxed">{exportError}</p>
          )}

          {/* 复制摘要人人可用，不再有「升级解锁分享」的预告条：
              导出图片的会员墙判在点击那一刻，那句话连同「现在去开通」一起给 */}
          {result.needsApproval && (
            <p className="text-xs text-center text-gray-400">
              复制后可直接粘贴到微信、短信等任意聊天窗口发送
            </p>
          )}

          {/* Close */}
          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-all border border-gray-200 text-gray-600 hover:bg-gray-50 whitespace-nowrap"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
}
